import React, { useEffect, useRef, useState } from "react";
import PhoneCallsService from "../../services/PhoneCallsService";

type RecordingPlayerProps = {
  callId: number;
  hasRecording: boolean;
};

/**
 * Recordings are behind an authenticated endpoint, and <audio src> cannot send
 * an Authorization header, so the audio is fetched as a blob and played from an
 * object URL instead.
 */
export default function RecordingPlayer({
  callId,
  hasRecording,
}: RecordingPlayerProps) {
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const objectUrlRef = useRef<string>("");

  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    [],
  );

  if (!hasRecording) {
    return <span className="text-xs text-gray-500">No recording</span>;
  }

  const loadRecording = async () => {
    setLoading(true);
    setError("");

    try {
      const blob = await PhoneCallsService().recording(callId);
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setAudioUrl(url);
    } catch {
      setError("Could not load this recording.");
    } finally {
      setLoading(false);
    }
  };

  if (audioUrl) {
    return (
      <audio
        controls
        preload="none"
        src={audioUrl}
        aria-label={`Recording of call ${callId}`}
        className="h-8 w-56"
      >
        <track kind="captions" />
      </audio>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={loadRecording}
        disabled={loading}
        className="px-3 py-1 rounded-md bg-indigo-600 text-xs text-white hover:bg-indigo-500 disabled:opacity-50"
      >
        {loading ? "Loading…" : "Play"}
      </button>
      {error ? <span className="text-xs text-red-400">{error}</span> : null}
    </div>
  );
}
